import React, { useState } from 'react';
import Header from '../../components/common/Header/Header';
import Footer from '../../components/common/Footer/Footer';
import SipCalculatorSection from '../../components/sections/InvestZoneSection/SipCalculatorSection';
import SipStepUpCalculatorSection from '../../components/sections/InvestZoneSection/SipStepUpCalculatorSection';
import EducationCalculatorSection from '../../components/sections/InvestZoneSection/EducationCalculatorSection';
import MarriageCalculatorSection from '../../components/sections/InvestZoneSection/MarriageCalculatorSection';
import RetirementCalculatorSection from '../../components/sections/InvestZoneSection/RetirementCalculatorSection';
import HolidayCalculatorSection from '../../components/sections/InvestZoneSection/HolidayCalculatorSection';
import SipDelayCalculatorSection from '../../components/sections/InvestZoneSection/SipDelayCalculatorSection';
import LumpsumCalculatorSection from '../../components/sections/InvestZoneSection/LumpsumCalculatorSection';
import SwpCalculatorSection from '../../components/sections/InvestZoneSection/SwpCalculatorSection';

const FinancialCalculator = () => {
  const [activeCalculator, setActiveCalculator] = useState(0);

  const calculatorDescriptions = {
    0: "Plan your systematic investment journey and calculate potential returns from regular monthly investments.",
    1: "Calculate how stepping up your SIP investments annually can accelerate your wealth creation.",
    2: "Plan and save for your child's education expenses with our comprehensive education calculator.",
    3: "Calculate the amount you need to save for your dream wedding and plan accordingly.",
    4: "Plan your retirement corpus and ensure a comfortable post-retirement life.",
    5: "Save for your dream vacation and calculate how much you need to invest monthly.",
    6: "Understand the impact of delaying your SIP investments on your long-term wealth creation."
  };

  const calculatorItems = [
    'SIP Calculator',
    'SIP Step-up Calculator',
    'Education Calculator',
    'Marriage Calculator',
    'Retirement Calculator',
    'Vacation Calculator',
    'SIP Delay Calculator',
    'Lumpsum Calculator',
    'SWP Calculator',
  ];

  const styles = {
    pageContainer: {
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#f8f9fa'
    },
    calculatorHeader: {
      background: '#1a2332',
      backgroundImage: `
        radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px),
        radial-gradient(circle at 75% 75%, rgba(255,255,255,0.05) 1px, transparent 1px),
        linear-gradient(45deg, rgba(255,255,255,0.02) 25%, transparent 25%),
        linear-gradient(-45deg, rgba(255,255,255,0.02) 25%, transparent 25%)
      `,
      backgroundSize: '20px 20px, 30px 30px, 40px 40px, 40px 40px',
      padding: '2rem 0',
      marginBottom: '0',
      position: 'relative',
      overflow: 'hidden',
      borderBottom: '1px solid rgba(255,255,255,0.1)'
    },
    calculatorHeaderContent: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 2rem',
      position: 'relative',
      zIndex: 2
    },
    calculatorName: {
      fontSize: '2rem',
      fontWeight: '600',
      color: '#ffffff',
      margin: '0',
      textAlign: 'center',
      textShadow: '0 1px 2px rgba(0,0,0,0.1)'
    },
    section: {
      padding: '2rem 0',
      backgroundColor: '#f8f9fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
      flex: 1
    },
    container: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 2rem'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: '350px 1fr',
      gap: '3rem',
      alignItems: 'flex-start'
    },
    leftColumn: {
      backgroundColor: '#ffffff',
      borderRadius: '10px',
      padding: '0',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
    },
    rightColumn: {
      backgroundColor: '#ffffff',
      borderRadius: '10px',
      padding: '2rem',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
    },
    calculatorTitle: {
      fontSize: '1.5rem',
      fontWeight: '600',
      color: '#1a1a1a',
      margin: '0 0 1.5rem 0',
      padding: '1.5rem 1.5rem 0 1.5rem'
    },
    calculatorList: {
      listStyle: 'none',
      padding: '0',
      margin: '0'
    },
    calculatorItem: {
      padding: '0.75rem 1.5rem',
      borderBottom: '1px solid #e5e7eb',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      color: '#374151',
      fontWeight: '500'
    },
    calculatorItemActive: {
      backgroundColor: '#22c55e', // Changed from red (#e53e3e) to green (#22c55e)
      color: 'white',
      fontWeight: '600'
    },
    calculatorItemHover: {
      backgroundColor: '#f3f4f6'
    },
    comingSoon: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      minHeight: '400px',
      color: '#6b7280'
    },
    comingSoonIcon: {
      fontSize: '3rem',
      marginBottom: '1rem',
      color: '#e5e7eb'
    },
    comingSoonTitle: {
      fontSize: '1.5rem',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '0.5rem'
    },
    comingSoonText: {
      fontSize: '1rem',
      lineHeight: '1.6'
    }
  };

  const renderCalculatorContent = () => {
    switch (activeCalculator) {
      case 0:
        return <SipCalculatorSection />;
      case 1:
        return <SipStepUpCalculatorSection />;
      case 2:
        return <EducationCalculatorSection />;
      case 3:
        return <MarriageCalculatorSection />;
      case 4:
        return <RetirementCalculatorSection />;
      case 5:
        return <HolidayCalculatorSection />;
      case 6:
        return <SipDelayCalculatorSection />;
      case 7:
        return <LumpsumCalculatorSection />;
      case 8:
        return <SwpCalculatorSection />;
     
      default:
        return (
          <div style={styles.comingSoon}>
            <div style={styles.comingSoonIcon}>🚧</div>
            <h3 style={styles.comingSoonTitle}>{calculatorItems[activeCalculator]}</h3>
            <p style={styles.comingSoonText}>
              This calculator is coming soon!<br />
              We're working hard to bring you more financial tools.
            </p>
          </div>
        );
    }
  };
  return (
    <>
      <style>{`
        .calculate-btn:hover {
          background-color:rgb(47, 220, 38) !important;
        }
        
        @media (max-width: 1024px) {
          .responsive-grid {
            grid-template-columns: 1fr !important;
            gap: 2rem !important;
          }
          .responsive-container {
            padding: 0 1.5rem !important;
          }
        }
        
        @media (max-width: 768px) {
          .responsive-container {
            padding: 0 1rem !important;
          }
          .responsive-grid {
            gap: 1.5rem !important;
          }
          .calculator-name {
            font-size: 1.5rem !important;
          }
        }
        
        @media (max-width: 480px) {
          .calculator-name {
            font-size: 1.25rem !important;
          }
        }
      `}</style>
      
      <div style={styles.pageContainer}>
        {/* Header */}
        <Header />
        
        {/* Calculator Header Section */}
        <section style={styles.calculatorHeader}>
          <div style={styles.calculatorHeaderContent} className="responsive-container">
            <h1 style={styles.calculatorName} className="calculator-name">
              {calculatorItems[activeCalculator]}
            </h1>
          </div>
        </section>
        
        {/* Main Content */}
        <main>
          <section style={styles.section}>
            <div style={{...styles.container}} className="responsive-container">
              <div style={{...styles.grid}} className="responsive-grid">
                {/* LEFT COLUMN - Calculator Menu */}
                <div style={styles.leftColumn}>
                  <h2 style={styles.calculatorTitle}>Financial Calculators</h2>
                  <ul style={styles.calculatorList}>
                    {calculatorItems.map((item, index) => (
                      <li
                        key={index}
                        style={{
                          ...styles.calculatorItem,
                          ...(index === activeCalculator ? styles.calculatorItemActive : {})
                        }}
                        onClick={() => setActiveCalculator(index)}
                        onMouseEnter={(e) => {
                          if (index !== activeCalculator) {
                            e.target.style.backgroundColor = styles.calculatorItemHover.backgroundColor;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (index !== activeCalculator) {
                            e.target.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* RIGHT COLUMN - Calculator Content */}
                <div style={styles.rightColumn}>
                  {renderCalculatorContent()}
                </div>
              </div>
            </div>
          </section>
        </main>
        
        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default FinancialCalculator;